import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Add, Delete, Edit } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';

import { useAppDispatch } from 'state/hooks';
import {
  UserProjectSubscription,
  useUserAdminDeleteMutation,
} from 'state/userAdminSlices';

import { ConfirmDeletePopup } from 'features/common/ConfirmDeletePopup';
import { StlButton } from 'features/common/StlButton/StlButton';
import StlTable from 'features/common/StlTable/StlTable';
import { useSubscription } from 'features/common/hooks/useSubscription';
import { ProjectStatusBadge } from 'features/user_admin/ProjectStatusBadge';
import { UserProjectsPanel } from 'features/user_admin/UserProjectsPanel';
import {
  formatAreaMi2,
  formatDate,
  navigateToNewRequest,
  projectUrl,
} from 'features/user_admin/common';
import { MRT_Cell, MRT_ColumnDef } from 'material-react-table';

import { InfoCard, InfoCardContainer, QuotaInfoBlock } from './InfoCard';
import { SubscriptionProjectsMap } from './SubscriptionProjectsMap';

import common_styles from './UserProjectsPanel.module.css';

function ProjectsTab({
  subscription,
}: {
  subscription: UserProjectSubscription;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [deleteUPR] = useUserAdminDeleteMutation();
  const [deletingUPR, setDeletingUPR] = useState(undefined);

  const project_rows = useMemo(() => {
    if (!subscription) return undefined;
    const rows = [];
    rows.push(
      ...subscription.projects.map((proj) => {
        return {
          link: projectUrl(subscription.subscription_id, proj),
          status: proj.status,
          name: proj.project_name,
          project_end: formatDate(proj.project_end) || '-',
        };
      }),
    );
    rows.push(
      ...subscription.project_requests.map((proj) => {
        return {
          link: projectUrl(subscription.subscription_id, proj),
          status: proj.status,
          name: proj.project_name,
          hash_id: proj.hash_id,
          project_end: formatDate(proj.project_end) || '-',
        };
      }),
    );
    return rows;
  }, [subscription]);

  const columns: MRT_ColumnDef<any>[] = useMemo(() => {
    return [
      {
        accessorKey: 'name',
        header: 'Name',
        muiTableBodyCellProps: ({ cell }) => ({
          title: cell.getValue<string>(), // shows full value on hover
        }),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ cell }: { cell: MRT_Cell }) => (
          <Box>
            <ProjectStatusBadge status={cell.getValue<string>()} />
          </Box>
        ),
      },
      { accessorKey: 'project_end', header: 'End Date' },
    ] as MRT_ColumnDef<any>[];
  }, []);

  const deleteUPRDraft = (hash_id) => {
    deleteUPR({ hash_id });
  };

  if (!subscription) {
    return null;
  }
  const addNewProject = () => {
    navigateToNewRequest(subscription.subscription_id, navigate, dispatch);
  };

  const sub_end_date = subscription?.template?.end_date;
  const end_date = sub_end_date || subscription?.projects[0]?.project_end;
  const { used_quota } = subscription;

  const handleDelete = () => {
    if (deletingUPR) deleteUPRDraft(deletingUPR.hash_id);
  };
  return (
    <>
      <Stack gap="1em" className={common_styles.content_block}>
        <Typography variant="h1" className={common_styles.subscription_name}>
          {subscription?.subscription_name}
        </Typography>
        <InfoCardContainer>
          {subscription?.template?.active_project_quota ? (
            <>
              <QuotaInfoBlock
                total_quota={subscription?.template?.active_project_quota}
                used_quota={used_quota}
                unusued_quota={
                  subscription.template.active_project_quota - used_quota
                }
              />
              <InfoCard
                num={formatAreaMi2(
                  subscription?.template?.project_max_area_mi2,
                  subscription.template.uses_metric,
                )}
                metric="Maximum Project Boundary Area"
              />
            </>
          ) : (
            <InfoCard num={subscription.projects.length} metric="Total" />
          )}
          {sub_end_date ? (
            <InfoCard
              num={formatDate(sub_end_date)}
              metric="Subscription End date"
            />
          ) : (
            end_date && (
              <InfoCard num={formatDate(end_date)} metric="Project End date" />
            )
          )}
        </InfoCardContainer>
      </Stack>
      <SubscriptionProjectsMap subscription={subscription} />
      <Stack gap="1em" className={common_styles.content_block}>
        <Stack
          direction="row"
          alignItems="center"
          gap="1em"
          justifyContent="space-between"
        >
          <Typography variant="h2">Projects</Typography>
          {subscription.can_create && (
            <StlButton
              handleClick={addNewProject}
              width={148}
              icon={<Add />}
              value="Add New Project"
            />
          )}
        </Stack>
        {project_rows && (
          <StlTable
            columns={columns}
            data={project_rows}
            muiTableBodyRowProps={({ row }) => ({
              onClick: (event) => navigate(row.original.link),
              className: common_styles.clickable_row,
            })}
            renderRowActions={({ row }) => {
              const isEditable =
                row.original.status === 'DRAFT' &&
                subscription.user_is_sub_admin;
              return (
                <>
                  {isEditable ? (
                    <>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(row.original.link);
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingUPR(row.original);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </>
                  ) : (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(row.original.link);
                      }}
                    >
                      Details
                    </Button>
                  )}
                </>
              );
            }}
          />
        )}
        <ConfirmDeletePopup
          objectType="project draft"
          isOpen={!!deletingUPR}
          setIsOpen={() => setDeletingUPR(undefined)}
          onApply={handleDelete}
          name={deletingUPR?.name}
        />
      </Stack>
    </>
  );
}

export function SubscriptionDetail() {
  const { subscription } = useSubscription();
  return (
    <UserProjectsPanel allowOverflow>
      <ProjectsTab subscription={subscription} />
    </UserProjectsPanel>
  );
}
